import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FileText, Video, Image as ImageIcon, BookOpen, Megaphone, Download,
  ExternalLink, Loader2, HandHeart, Award, ArrowRight, Sparkles, ShieldCheck,
  Search, CheckCircle2, XCircle, AlertCircle, LayoutDashboard, Trophy,
} from 'lucide-react'
import { PortalLayout } from '../../components/portal/PortalLayout'
import { Seo } from '../../components/portal/Seo'
import { Container, Section, SectionHeading, Breadcrumbs, Card, Badge, EmptyState, Alert, Button } from '../../components/portal/ui'
import {
  listResources,
  RESOURCE_TYPE_LABELS,
  type Resource,
  type ResourceType,
} from '../../services/resourceService'
import {
  verifyCertificate, myCertificates,
  type CertificateVerify, type Certificate,
} from '../../services/certificateService'
import { useAuthStore } from '../../stores/authStore'

// Volunteer resource sections in the order the user specified.
const VOL_SECTIONS: { type: ResourceType; icon: typeof FileText }[] = [
  { type: 'TRAINING', icon: Video },
  { type: 'IEC_MATERIAL', icon: ImageIcon },
  { type: 'CAMPAIGN_TOOLKIT', icon: Megaphone },
]

const TYPE_ICON: Record<string, typeof FileText> = {
  GUIDELINE: FileText, REPORT: FileText, TRAINING: Video, INFOGRAPHIC: ImageIcon,
  VIDEO: Video, PUBLICATION: BookOpen, NOTIFICATION: FileText, MEDIA: ImageIcon,
  IEC_MATERIAL: ImageIcon, CAMPAIGN_TOOLKIT: Megaphone, REPORTING_FORMAT: FileText, OTHER: FileText,
}

export default function VolunteerHubPage() {
  const { t, i18n } = useTranslation()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)
  const [items, setItems] = useState<Resource[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // certificate verify form
  const [verifyNum, setVerifyNum] = useState('')
  const [verifyResult, setVerifyResult] = useState<CertificateVerify | null>(null)
  const [verifyError, setVerifyError] = useState<string | null>(null)
  const [verifying, setVerifying] = useState(false)

  // my certificates (authed)
  const [mine, setMine] = useState<Certificate[]>([])
  const [mineLoading, setMineLoading] = useState(false)
  const [mineError, setMineError] = useState<string | null>(null)

  const typeLabel = (k: string) => t(`pages.resources.types.${k}`, { defaultValue: RESOURCE_TYPE_LABELS[k as ResourceType] ?? k })

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const res = await listResources({ audience: 'VOLUNTEER', size: 60 })
      setItems(res.content)
    } catch (e: any) {
      setError(e?.response?.data?.message || t('pages.volunteerHub.error', { defaultValue: 'Could not load materials. Please try again.' }))
    } finally {
      setLoading(false)
    }
  }, [t])

  const loadMine = useCallback(async () => {
    if (!isAuthenticated) return
    setMineLoading(true); setMineError(null)
    try {
      setMine(await myCertificates())
    } catch (e: any) {
      setMineError(e?.response?.data?.message || t('pages.volunteerHub.mineError', { defaultValue: 'Could not load your certificates.' }))
    } finally {
      setMineLoading(false)
    }
  }, [isAuthenticated, t])

  useEffect(() => { load() }, [load, i18n.language])
  useEffect(() => { loadMine() }, [loadMine])

  const onVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!verifyNum.trim()) return
    setVerifying(true); setVerifyResult(null); setVerifyError(null)
    try {
      setVerifyResult(await verifyCertificate(verifyNum.trim()))
    } catch (err: any) {
      setVerifyError(err?.response?.data?.message || t('pages.volunteerHub.verifyNotFound', { defaultValue: 'Certificate not found or invalid. Please check the number and try again.' }))
    } finally {
      setVerifying(false)
    }
  }

  const target = (r: Resource) => r.externalUrl || r.fileUrl || ''
  const byType = (tp: ResourceType) => items.filter((r) => r.resourceType === tp)

  const renderResourceCard = (r: Resource) => {
    const Icon = TYPE_ICON[r.resourceType] ?? FileText
    const href = target(r)
    const Tag = href ? 'a' : 'div'
    return (
      <Tag
        key={r.id}
        {...(href ? { href, target: '_blank', rel: 'noopener noreferrer' } : {})}
        className={`block ${href ? 'hover:shadow-md transition-shadow cursor-pointer' : ''}`}
      >
        <Card hover={!!href} className="p-5 h-full flex flex-col">
          <div className="flex items-start gap-3 mb-3">
            <span className="w-10 h-10 rounded-lg bg-[rgb(240_252_255)] text-[rgb(15_76_129)] flex items-center justify-center shrink-0" aria-hidden="true">
              <Icon className="w-5 h-5" />
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-snow-900 line-clamp-2">{r.title}</h3>
              <p className="text-xs text-snow-500 mt-0.5">{typeLabel(r.resourceType)}</p>
            </div>
          </div>
          {r.description && <p className="text-sm text-snow-600 line-clamp-3 flex-1">{r.description}</p>}
          <div className="flex flex-wrap items-center gap-1.5 mt-3">
            {r.fileType && <Badge tone="slate">{r.fileType}</Badge>}
            {!href && <Badge tone="amber">{t('pages.volunteerHub.beingCurated', { defaultValue: 'Being curated' })}</Badge>}
          </div>
          {r.sourceOrg && <p className="text-[11px] text-snow-500 mt-2">{r.sourceOrg}</p>}
          {href && (
            <p className="text-xs text-[rgb(15_76_129)] mt-3 inline-flex items-center gap-1">
              {r.externalUrl ? t('pages.resources.openResource', { defaultValue: 'Open resource' }) : t('pages.resources.download', { defaultValue: 'Download' })}
              <ExternalLink className="w-3 h-3" aria-hidden="true" />
            </p>
          )}
        </Card>
      </Tag>
    )
  }

  const renderSection = (tp: ResourceType, icon: typeof FileText, titleKey: string) => {
    const list = byType(tp)
    const Icon = icon
    return (
      <div className="mb-12">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-9 h-9 rounded-lg bg-[rgb(232_241_252)] text-[rgb(15_76_129)] flex items-center justify-center" aria-hidden="true">
            <Icon className="w-5 h-5" />
          </span>
          <h2 className="text-xl font-bold text-snow-900">{t(titleKey)}</h2>
        </div>
        {loading ? (
          <div className="flex items-center text-snow-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t('common.loading', { defaultValue: 'Loading…' })}</div>
        ) : list.length === 0 ? (
          <EmptyState
            icon={<Download className="w-6 h-6" />}
            title={t('pages.volunteerHub.emptyTitle', { defaultValue: 'Materials coming soon' })}
            description={t('pages.volunteerHub.emptyDesc', { defaultValue: 'Verified materials for this section are being added. Please check back shortly.' })}
          />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {list.map(renderResourceCard)}
          </div>
        )}
      </div>
    )
  }

  return (
    <PortalLayout>
      <Seo
        title={t('pages.volunteerHub.seo.title', { defaultValue: 'Volunteer Hub — TB Mukt Bharat' })}
        description={t('pages.volunteerHub.seo.description', { defaultValue: 'Volunteer to support TB elimination: register, training, campaign activities, downloadable materials and recognition certificates.' })}
        canonicalPath="/volunteer"
        breadcrumbs={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.volunteerHub', { defaultValue: 'Volunteer Hub' }), path: '/volunteer' }]}
      />

      <Section className="pb-6">
        <Container>
          <Breadcrumbs items={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.volunteerHub', { defaultValue: 'Volunteer Hub' }) }]} />
          <SectionHeading
            as="h1"
            eyebrow={t('pages.volunteerHub.hero.eyebrow', { defaultValue: 'For individuals' })}
            title={t('pages.volunteerHub.hero.title', { defaultValue: 'Volunteer Hub' })}
            description={t('pages.volunteerHub.hero.description', { defaultValue: 'Lend your time and skills to the TB elimination movement — register, get trained, join campaigns and earn recognition for your contribution.' })}
          />
          <div className="flex flex-wrap gap-3 mt-4">
            <Button to="/become-a-supporter?type=VOLUNTEER" variant="primary" icon={<HandHeart className="w-4 h-4" />} className="min-h-[44px]">
              {t('pages.volunteerHub.registerCta', { defaultValue: 'Register as a volunteer' })}
            </Button>
            {isAuthenticated && (
              <Button to="/volunteer-dashboard" variant="outline" icon={<LayoutDashboard className="w-4 h-4" />}>
                {t('pages.volunteerHub.dashboardCta', { defaultValue: 'My volunteer dashboard' })}
              </Button>
            )}
          </div>
        </Container>
      </Section>

      <Section className="bg-white pt-8">
        <Container>
          {error && <Alert tone="warning" title={error} className="mb-6" />}

          <Alert tone="info" title={t('pages.volunteerHub.curatedNoticeTitle', { defaultValue: 'Materials are being curated' })} className="mb-8">
            {t('pages.volunteerHub.curatedNoticeBody', { defaultValue: 'The resources below are sample placeholders. Verified documents from official sources will be attached by the TB Mukt Bharat team. No government content is fabricated.' })}
          </Alert>

          {/* Registration */}
          <Card className="p-6 mb-12 border-2 border-[rgb(191_237_255)]">
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <span className="w-12 h-12 rounded-xl bg-[rgb(15_76_129)] text-white flex items-center justify-center shrink-0" aria-hidden="true">
                <HandHeart className="w-6 h-6" />
              </span>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-snow-900">{t('pages.volunteerHub.registration.title', { defaultValue: 'Register as a volunteer' })}</h2>
                <p className="text-snow-600 text-sm mt-1">{t('pages.volunteerHub.registration.body', { defaultValue: 'Sign up as a volunteer supporter to join awareness drives, patient support and campaign activities. Registration is reviewed by the TB Mukt Bharat team.' })}</p>
                <div className="mt-3">
                  <Button to="/become-a-supporter?type=VOLUNTEER" variant="primary" icon={<ArrowRight className="w-4 h-4" />} className="min-h-[44px]">
                    {t('pages.volunteerHub.registration.button', { defaultValue: 'Start volunteer registration' })}
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Training + downloadable materials */}
          {VOL_SECTIONS.map((s) => renderSection(s.type, s.icon, `pages.volunteerHub.sections.${s.type}`))}

          {/* Campaign activities */}
          <div className="mb-12">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-9 h-9 rounded-lg bg-[rgb(232_241_252)] text-[rgb(15_76_129)] flex items-center justify-center" aria-hidden="true">
                <Megaphone className="w-5 h-5" />
              </span>
              <h2 className="text-xl font-bold text-snow-900">{t('pages.volunteerHub.sections.campaignActivities', { defaultValue: 'Campaign activities' })}</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <Card className="p-6 flex flex-col">
                <Trophy className="w-7 h-7 text-[rgb(15_76_129)] mb-3" aria-hidden="true" />
                <h3 className="font-semibold text-snow-900">{t('pages.volunteerHub.campaigns.browseTitle', { defaultValue: 'Browse active campaigns' })}</h3>
                <p className="text-sm text-snow-600 mt-2 flex-1">{t('pages.volunteerHub.campaigns.browseBody', { defaultValue: 'See ongoing TB awareness and support campaigns you can join in your area or online.' })}</p>
                <div className="mt-4"><Button to="/campaigns" variant="outline" icon={<ArrowRight className="w-4 h-4" />} className="min-h-[44px]">{t('pages.volunteerHub.campaigns.browseBtn', { defaultValue: 'View campaigns' })}</Button></div>
              </Card>
              {/* Create-campaign card: any logged-in user may create a campaign. */}
              <Card className="p-6 flex flex-col">
                <Sparkles className="w-7 h-7 text-[rgb(15_76_129)] mb-3" aria-hidden="true" />
                <h3 className="font-semibold text-snow-900">{t('pages.volunteerHub.campaigns.createTitle', { defaultValue: 'Start a campaign' })}</h3>
                <p className="text-sm text-snow-600 mt-2 flex-1">{t('pages.volunteerHub.campaigns.createBody', { defaultValue: 'Launch a community awareness or fundraising campaign for TB elimination.' })}</p>
                <div className="mt-4"><Button to="/campaigns/create" variant="outline" icon={<ArrowRight className="w-4 h-4" />} className="min-h-[44px]">{t('pages.volunteerHub.campaigns.createBtn', { defaultValue: 'Create campaign' })}</Button></div>
              </Card>
            </div>
          </div>

          {/* Certificates / recognition — public verify + my certificates */}
          <div className="mb-12">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-9 h-9 rounded-lg bg-[rgb(232_252_243)] text-[rgb(0_138_88)] flex items-center justify-center" aria-hidden="true">
                <Award className="w-5 h-5" />
              </span>
              <h2 className="text-xl font-bold text-snow-900">{t('pages.volunteerHub.sections.certificates', { defaultValue: 'Certificates & recognition' })}</h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Public verify */}
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="w-5 h-5 text-[rgb(0_138_88)]" aria-hidden="true" />
                  <h3 className="font-semibold text-snow-900">{t('pages.volunteerHub.verify.title', { defaultValue: 'Verify a certificate' })}</h3>
                </div>
                <p className="text-sm text-snow-600 mb-4">{t('pages.volunteerHub.verify.body', { defaultValue: 'Enter a certificate number to confirm it was issued by TB Mukt Bharat. No email or personal data is shown.' })}</p>
                <form onSubmit={onVerify} className="flex flex-col sm:flex-row gap-2">
                  <label htmlFor="cert-num" className="sr-only">{t('pages.volunteerHub.verify.label', { defaultValue: 'Certificate number' })}</label>
                  <input
                    id="cert-num"
                    type="text"
                    value={verifyNum}
                    onChange={(e) => setVerifyNum(e.target.value)}
                    placeholder="TBMK-VOL-XXXXXXXX"
                    className="input flex-1"
                    autoComplete="off"
                  />
                  <Button type="submit" variant="primary" disabled={verifying || !verifyNum.trim()} icon={verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} className="min-h-[44px]">
                    {t('pages.volunteerHub.verify.button', { defaultValue: 'Verify' })}
                  </Button>
                </form>

                {verifyError && (
                  <Alert tone="warning" title={t('pages.volunteerHub.verify.failTitle', { defaultValue: 'Not verified' })} className="mt-4">
                    <span className="inline-flex items-center gap-2"><XCircle className="w-4 h-4" /> {verifyError}</span>
                  </Alert>
                )}
                {verifyResult && (
                  <div className="mt-4 rounded-lg border border-[rgb(191_237_255)] bg-[rgb(240_252_255)] p-4">
                    <div className="flex items-center gap-2 mb-2">
                      {verifyResult.valid ? <CheckCircle2 className="w-5 h-5 text-[rgb(0_138_88)]" /> : <AlertCircle className="w-5 h-5 text-amber-600" />}
                      <span className="font-semibold text-snow-900">
                        {verifyResult.valid
                          ? t('pages.volunteerHub.verify.valid', { defaultValue: 'Valid certificate' })
                          : t('pages.volunteerHub.verify.revoked', { defaultValue: 'Certificate revoked' })}
                      </span>
                    </div>
                    <dl className="text-sm text-snow-700 space-y-1">
                      <div><dt className="inline font-medium">{t('pages.volunteerHub.verify.name', { defaultValue: 'Awarded to' })}: </dt><dd className="inline">{verifyResult.volunteerName}</dd></div>
                      <div><dt className="inline font-medium">{t('pages.volunteerHub.verify.award', { defaultValue: 'Award' })}: </dt><dd className="inline">{verifyResult.awardTitle}</dd></div>
                      {verifyResult.programmeTag && <div><dt className="inline font-medium">{t('pages.volunteerHub.verify.programme', { defaultValue: 'Programme' })}: </dt><dd className="inline">{verifyResult.programmeTag}</dd></div>}
                      <div><dt className="inline font-medium">{t('pages.volunteerHub.verify.issued', { defaultValue: 'Issued' })}: </dt><dd className="inline">{verifyResult.issueDate}{verifyResult.issuedByName ? ` · ${verifyResult.issuedByName}` : ''}</dd></div>
                      <div><dt className="inline font-medium">{t('pages.volunteerHub.verify.number', { defaultValue: 'Number' })}: </dt><dd className="inline font-mono">{verifyResult.certificateNumber}</dd></div>
                    </dl>
                  </div>
                )}
                <p className="text-[11px] text-snow-500 mt-3">{t('pages.volunteerHub.verify.disclaimer', { defaultValue: 'Certificates recognise voluntary contribution and imply no government accreditation.' })}</p>
              </Card>

              {/* My certificates */}
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Award className="w-5 h-5 text-[rgb(15_76_129)]" aria-hidden="true" />
                  <h3 className="font-semibold text-snow-900">{t('pages.volunteerHub.mine.title', { defaultValue: 'My certificates' })}</h3>
                </div>
                {!isAuthenticated ? (
                  <div>
                    <p className="text-sm text-snow-600 mb-4">{t('pages.volunteerHub.mine.signin', { defaultValue: 'Sign in to view certificates issued to your email address.' })}</p>
                    <Button to="/login" variant="primary" icon={<ArrowRight className="w-4 h-4" />} className="min-h-[44px]">{t('pages.volunteerHub.mine.signinBtn', { defaultValue: 'Sign in' })}</Button>
                  </div>
                ) : mineLoading ? (
                  <div className="flex items-center text-snow-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t('common.loading', { defaultValue: 'Loading…' })}</div>
                ) : mineError ? (
                  <Alert tone="warning" title={mineError} />
                ) : mine.length === 0 ? (
                  <EmptyState
                    icon={<Award className="w-6 h-6" />}
                    title={t('pages.volunteerHub.mine.emptyTitle', { defaultValue: 'No certificates yet' })}
                    description={t('pages.volunteerHub.mine.emptyDesc', { defaultValue: 'Certificates you earn through volunteer activities will appear here.' })}
                  />
                ) : (
                  <ul className="space-y-3">
                    {mine.map((c) => (
                      <li key={c.id} className="rounded-lg border border-snow-200 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-snow-900 line-clamp-1">{c.awardTitle}</p>
                            <p className="text-xs text-snow-500 mt-0.5 font-mono">{c.certificateNumber}</p>
                            <p className="text-xs text-snow-500 mt-1">{t('pages.volunteerHub.mine.issued', { defaultValue: 'Issued' })} {c.issueDate}{c.issuedByName ? ` · ${c.issuedByName}` : ''}</p>
                          </div>
                          <Badge tone={c.status === 'ACTIVE' ? 'green' : 'slate'}>
                            {c.status === 'ACTIVE' ? t('pages.volunteerHub.mine.active', { defaultValue: 'Active' }) : t('pages.volunteerHub.mine.revoked', { defaultValue: 'Revoked' })}
                          </Badge>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
          </div>

          {/* Volunteer dashboard teaser */}
          <Card className="p-6 border-2 border-[rgb(191_237_255)]">
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <span className="w-12 h-12 rounded-xl bg-[rgb(15_76_129)] text-white flex items-center justify-center shrink-0" aria-hidden="true">
                <LayoutDashboard className="w-6 h-6" />
              </span>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-snow-900">{t('pages.volunteerHub.dashboard.title', { defaultValue: 'Volunteer dashboard' })}</h2>
                <p className="text-snow-600 text-sm mt-1">{t('pages.volunteerHub.dashboard.body', { defaultValue: 'Track your registrations, campaigns and certificates in one place.' })}</p>
                <div className="mt-3">
                  <Button to={isAuthenticated ? '/volunteer-dashboard' : '/login'} variant="primary" icon={<ArrowRight className="w-4 h-4" />} className="min-h-[44px]">
                    {isAuthenticated
                      ? t('pages.volunteerHub.dashboard.open', { defaultValue: 'Open dashboard' })
                      : t('pages.volunteerHub.dashboard.signin', { defaultValue: 'Sign in to open dashboard' })}
                  </Button>
                </div>
                {isAuthenticated && user && (
                  <p className="text-xs text-snow-500 mt-3">{t('pages.volunteerHub.dashboard.signedInAs', { defaultValue: 'Signed in as' })} {user.email}</p>
                )}
              </div>
            </div>
          </Card>
        </Container>
      </Section>
    </PortalLayout>
  )
}